import React from 'react';
import PropTypes from 'prop-types';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';

// Registrar componentes de Chart.js
if (ChartJS.register) {
  ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    Title
  );
}

const ProcessEfficiencyChart = ({ procesos = [] }) => {
  // Función para formatear tiempo de manera legible
  const formatTime = (hours) => {
    if (typeof hours !== 'number' || hours < 0) return 'N/A';
    
    if (hours === 0) {
      return '0 min';
    } else if (hours < 1) {
      // Menos de 1 hora: mostrar en minutos
      const minutes = Math.round(hours * 60);
      return `${minutes} min`;
    } else if (hours < 24) {
      // Menos de 24 horas: mostrar horas con minutos
      const totalMinutes = Math.round(hours * 60);
      const hoursPart = Math.floor(totalMinutes / 60);
      const minutesPart = totalMinutes % 60;
      
      if (minutesPart === 0) {
        return hoursPart === 1 ? '1 hora' : `${hoursPart} horas`;
      } else {
        return `${hoursPart}h ${minutesPart}min`;
      }
    } else {
      // 24 horas o más: mostrar días, horas y minutos
      const totalMinutes = Math.round(hours * 60);
      const days = Math.floor(totalMinutes / (24 * 60));
      const remainingMinutes = totalMinutes % (24 * 60);
      const hoursPart = Math.floor(remainingMinutes / 60);
      const minutesPart = remainingMinutes % 60;
      
      let result = '';
      if (days > 0) {
        result += days === 1 ? '1 día' : `${days} días`;
      }
      if (hoursPart > 0) {
        if (result) result += ', ';
        result += hoursPart === 1 ? '1 hora' : `${hoursPart} horas`;
      }
      if (minutesPart > 0) {
        if (result) result += ', ';
        result += `${minutesPart} min`;
      }
      
      return result || '0 min';
    }
  };

  // Validar que procesos sea un array válido
  const procesosValidos = Array.isArray(procesos) ? procesos : [];
  
  // Calcular porcentajes si no están incluidos
  const totalTiempo = procesosValidos.reduce((sum, proceso) => sum + (proceso.tiempo_proceso || 0), 0);
  
  const procesosConPorcentaje = procesosValidos.map(proceso => ({
    ...proceso,
    porcentaje: totalTiempo > 0 ? Math.round((proceso.tiempo_proceso || 0) / totalTiempo * 100) : 0,
  }));

  // Colores para los diferentes procesos
  const colors = [
    '#FF6384', // Rojo
    '#36A2EB', // Azul
    '#FFCE56', // Amarillo
    '#4BC0C0', // Turquesa
    '#9966FF', // Púrpura
    '#FF9F40', // Naranja
    '#FF6384', // Rosa
    '#C9CBCF', // Gris
  ];

  // Preparar datos para el gráfico
  const chartData = {
    labels: procesosConPorcentaje.map(proceso => proceso.nombre || 'Proceso sin nombre'),
    datasets: [
      {
        data: procesosConPorcentaje.map(proceso => proceso.porcentaje),
        backgroundColor: colors.slice(0, procesosConPorcentaje.length),
        borderColor: colors.slice(0, procesosConPorcentaje.length).map(color => color),
        borderWidth: 2,
        hoverOffset: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          font: {
            size: 12,
          },
        },
      },
      title: {
        display: true,
        text: 'Distribución de Tiempo por Proceso',
        font: {
          size: 16,
          weight: 'bold',
        },
        padding: {
          top: 10,
          bottom: 20,
        },
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function(context) {
            const proceso = procesosConPorcentaje[context.dataIndex];
            const tiempo = proceso?.tiempo_proceso || 0;
            const porcentaje = proceso?.porcentaje || 0;
            return `${context.label}: ${formatTime(tiempo)} (${porcentaje}%)`;
          },
        },
      },
    },
    cutout: '50%',
    animation: {
      animateRotate: true,
      animateScale: true,
    },
  };

  // Si no hay procesos, mostrar mensaje
  if (!procesosValidos || procesosValidos.length === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: '300px' }}>
        <div className="text-center">
          <i className="fas fa-chart-pie fa-3x text-muted mb-3"></i>
          <p className="text-muted">No hay datos de procesos para mostrar</p>
        </div>
      </div>
    );
  }

  // Si todos los procesos tienen 0% de tiempo, mostrar mensaje
  if (totalTiempo === 0) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ height: '300px' }}>
        <div className="text-center">
          <i className="fas fa-chart-pie fa-3x text-muted mb-3"></i>
          <p className="text-muted">No hay tiempo registrado en los procesos</p>
        </div>
      </div>
    );
  }

  return (
    <div className="efficiency-chart" style={{ height: '400px' }}>
      <Doughnut data={chartData} options={options} />
    </div>
  );
};

ProcessEfficiencyChart.propTypes = {
  procesos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      nombre: PropTypes.string,
      tiempo_proceso: PropTypes.number,
      porcentaje: PropTypes.number,
    })
  ),
};

export default ProcessEfficiencyChart;
